import axios from 'axios';
import authHeader from '../auth-header';
import { getUrl } from '../service-utils';
import { IAddDayModel } from '../../components/modals/day/use-add-day';
import { IEditDayModel } from '../../components/modals/day/use-edit-day';

const API_URL = getUrl();

class DayService {
  /**
   * Create a new day in an itinerary
   * @param tripId - The ID of the trip
   * @param itineraryId - The ID of the itinerary
   * @param dayData - The day data to create
   */
  public async createDay(
    tripId: string,
    itineraryId: string,
    dayData: IAddDayModel
  ) {
    try {
      const response = await axios.post(
        `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days`,
        dayData,
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error creating day:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get a specific day by ID
   * @param tripId - The ID of the trip
   * @param itineraryId - The ID of the itinerary
   * @param dayId - The ID of the day
   */
  public async getDayById(tripId: string, itineraryId: string, dayId: string) {
    try {
      const response = await axios.get(
        `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days/${dayId}`,
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error fetching day:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Update an existing day
   * @param tripId - The ID of the trip
   * @param itineraryId - The ID of the itinerary
   * @param dayId - The ID of the day to update
   * @param dayData - The updated day data
   */
  public async updateDay(
    tripId: string,
    itineraryId: string,
    dayId: string,
    dayData: Partial<IEditDayModel>
  ) {
    try {
      const response = await axios.put(
        `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days/${dayId}`,
        dayData,
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error updating day:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Delete a day
   * @param tripId - The ID of the trip
   * @param itineraryId - The ID of the itinerary
   * @param dayId - The ID of the day to delete
   */
  public async deleteDay(tripId: string, itineraryId: string, dayId: string) {
    try {
      const response = await axios.delete(
        `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days/${dayId}`,
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error deleting day:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Get all days for an itinerary
   * @param tripId - The ID of the trip
   * @param itineraryId - The ID of the itinerary
   */
  public async getDaysByItinerary(tripId: string, itineraryId: string) {
    try {
      const response = await axios.get(
        `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days`,
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error fetching days:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Reorder days in an itinerary
   * @param tripId - The ID of the trip
   * @param itineraryId - The ID of the itinerary
   * @param dayOrders - Array of day IDs in new order
   */
  public async reorderDays(
    tripId: string,
    itineraryId: string,
    dayOrders: string[]
  ) {
    try {
      const response = await axios.put(
        `${API_URL}trips/${tripId}/itineraries/${itineraryId}/days/reorder`,
        { dayOrders },
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error reordering days:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }
}

const dayService = new DayService();
export default dayService;
